import React from 'react';
import { css } from 'emotion';
import { spacer40 } from '@spotify-internal/encore-web';
import { LoadingSpinner } from '@spotify-internal/encore-web/advertising';
import {
  SavedCuePoint,
  SavedCuePoints,
} from 'screens/EpisodeScreen/components/CuePoints/types';
import { useSAIWaveformContext } from 'components/AdInsertion/context';
import { UploadErrorIcon } from 'shared/Icon/components/UploadErrorIcon';
import { getPlacementType } from 'screens/EpisodeScreen/components/CuePoints/utils';
import {
  IconWrapper,
  RemoveButton,
} from 'screens/EpisodeScreen/components/CuePoints/styles';
import CloseIcon from 'shared/Icon/components/XIcon';
import { TriangleWarningIcon } from 'client/shared/Icon/components/TriangleWarningIcon';
import { ENABLE_PAYWALLS_DISABLED_ADS_WARNING } from 'client/components/AdInsertion/components/AdInsertionPaywallDisabledAdsWarning';
import { useFetchEpisodeOverview } from 'client/hooks/episodes/useFetchEpisodeOverview';
import { useT } from 'client/i18n/hooks/useT';
import { useParams } from 'react-router-dom';
import {
  Table,
  CuePointTableWrapper,
  CuePointTableContainer,
  CuePointTableDisabledAdsWarning,
  TableHeadRow,
  TableBodyRow,
  AdId,
  DisabledPill,
  EditLink,
  Container,
} from './styles';

type CuePointsTableProps = {
  cuePoints?: SavedCuePoints;
  duration: number;
  removeCuePoint: (cuePoint: SavedCuePoint) => void;
  onClickEditCuePoint: (cuePoint: SavedCuePoint) => void;
};

export function CuePointsTable({
  cuePoints,
  duration,
  removeCuePoint,
  onClickEditCuePoint,
}: CuePointsTableProps) {
  const { onTableRowClick } = useSAIWaveformContext();
  const { podcastEpisodeId } = useParams<{ podcastEpisodeId: string }>();
  const { episodeOverviewData } = useFetchEpisodeOverview({
    webEpisodeId: podcastEpisodeId,
  });
  const t = useT();

  const showDisabledAdsWarning =
    ENABLE_PAYWALLS_DISABLED_ADS_WARNING &&
    cuePoints &&
    cuePoints.length > 0 &&
    episodeOverviewData &&
    episodeOverviewData.isPaywallContent;

  return (
    <CuePointTableContainer>
      <CuePointTableWrapper
        hasRows={cuePoints !== undefined && cuePoints.length > 0}
      >
        {cuePoints && cuePoints.length ? (
          <Table aria-label="Table of cue points">
            <thead>
              <TableHeadRow>
                <th scope="col" colSpan={1}>
                  {t(
                    'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.IdColumnText',
                    'ID',
                    'Cue points table id column'
                  )}
                </th>
                <th scope="col" colSpan={1}>
                  {t(
                    'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.AdTypeColumnText',
                    'AD TYPE',
                    'Cue points table ad type column'
                  )}
                </th>
                <th scope="col" colSpan={1}>
                  {t(
                    'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.TimestampColumnText',
                    'TIMESTAMP',
                    'Cue points table timestamp column'
                  )}
                </th>
                <th scope="col" colSpan={4}>
                  {t(
                    'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.CountColumnText',
                    'COUNT',
                    'Cue points table count column'
                  )}
                </th>
                <th
                  colSpan={1}
                  aria-label={t(
                    'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.RowActionColumnAriaLabel',
                    'Row action',
                    'Row action column screenreader information'
                  )}
                />
              </TableHeadRow>
            </thead>
            <tbody>
              {cuePoints
                .sort((a, b) => a.startTime - b.startTime) // sort timestamp ascending
                .map((cuePoint: SavedCuePoint, index) => {
                  const {
                    startTime,
                    startTimeString,
                    adCount,
                    placementType,
                    isRemoving,
                  } = cuePoint;

                  const isDisabled = startTime > duration && duration !== 0;

                  return (
                    <TableBodyRow
                      key={startTime}
                      isDisabled={isDisabled}
                      onClick={() => !isDisabled && onTableRowClick(cuePoint)}
                      onKeyDown={e => {
                        if (e.keyCode === 13) {
                          onTableRowClick(cuePoint);
                        }
                      }}
                      tabIndex={0}
                      aria-label={
                        isDisabled
                          ? t(
                              'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.DisabledPlacementTypeColumnAriaLabel',
                              'The {placementType} timestamp at {startTimeString} is invalid because it is not within the duration of the episode',
                              'Playment type column disabled screenreader information',
                              {
                                placementType: getPlacementType(
                                  placementType,
                                  t
                                ),
                                startTimeString,
                              }
                            )
                          : t(
                              'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.PlacementTypeColumnAriaLabel',
                              'Click row to move cursor to {placementType} timestamp at {startTimeString} on waveform',
                              'Playment type column screenreader information',
                              {
                                placementType: getPlacementType(
                                  placementType,
                                  t
                                ),
                                startTimeString,
                              }
                            )
                      }
                    >
                      <td colSpan={1}>
                        <AdId placementType={placementType}>
                          {String.fromCharCode(65 + index)}
                        </AdId>
                      </td>
                      <td colSpan={1}>{getPlacementType(placementType, t)}</td>
                      <td colSpan={1}>{startTimeString}</td>
                      <td colSpan={4}>{adCount}</td>
                      <td colSpan={1}>
                        {isRemoving ? (
                          <div data-testid="loadingSpinner">
                            <LoadingSpinner diameter={spacer40} />
                          </div>
                        ) : (
                          <>
                            {isDisabled && (
                              <DisabledPill>
                                {t(
                                  'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.DisabledPillText',
                                  'Disabled',
                                  'Disabled label'
                                )}
                              </DisabledPill>
                            )}
                            <EditLink
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.keyCode === 13) {
                                  onClickEditCuePoint(cuePoint);
                                }
                              }}
                              onClick={() => {
                                onClickEditCuePoint(cuePoint);
                              }}
                            >
                              {t(
                                'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.EditLinkText',
                                'edit',
                                'Edit link text'
                              )}
                            </EditLink>
                            <RemoveButton
                              type="button"
                              aria-label={t(
                                'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.RemoveButtonAriaLabel',
                                'delete cue point with timestamp {startTimeString}',
                                'Remove cue point button screenreader information',
                                { startTimeString }
                              )}
                              onClick={() => {
                                removeCuePoint(cuePoint);
                              }}
                            >
                              <IconWrapper>
                                <CloseIcon
                                  className={css`
                                    width: 100%;
                                  `}
                                />
                              </IconWrapper>
                            </RemoveButton>
                          </>
                        )}
                      </td>
                    </TableBodyRow>
                  );
                })}
            </tbody>
          </Table>
        ) : (
          <Container>
            <UploadErrorIcon
              fillColor="#5F6368"
              className={css`
                width: 30px;
                height: 35px;
              `}
            />
            <h3>
              {t(
                'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.NoAdsPlacedHeader',
                'No ads placed',
                'No ads placed header text'
              )}
            </h3>
            <p>
              {t(
                'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.NoAdsPlacedMessage',
                'Enter a timestamp, count, and ad type to insert an ad slot',
                'No ads placed message'
              )}
            </p>
          </Container>
        )}
      </CuePointTableWrapper>
      {showDisabledAdsWarning && (
        <CuePointTableDisabledAdsWarning>
          <TriangleWarningIcon fillColor="#000" />
          {t(
            'EpisodeScreen.AdInsertion.Waveform.CuePoints.CuePointTable.AdsDisabledForSubscriptionEpisodesWarning',
            'Ads by Anchor features will be disabled for subscription episodes.',
            'Ads disabled for subscription episodes warning'
          )}
        </CuePointTableDisabledAdsWarning>
      )}
    </CuePointTableContainer>
  );
}
